import storage from 'good-storage'

// ─────────────────────────────────────────────
// 通配符匹配（与 Swift matchWildcard 逻辑完全一致）
// 支持 * 匹配任意字符序列、? 匹配单个字符
// 示例：'/auth/evaluation/*' 匹配 '/auth/evaluation/queryPlan'
// ─────────────────────────────────────────────
export function matchWildcard (pattern, text) {
  if (!pattern || !text) return false
  if (pattern === text) return true

  const p = Array.from(pattern)
  const t = Array.from(text)
  const pLen = p.length
  const tLen = t.length

  let pIdx = 0
  let tIdx = 0
  let starIdx = -1
  let matchIdx = -1

  while (tIdx < tLen) {
    if (pIdx < pLen && (p[pIdx] === t[tIdx] || p[pIdx] === '?')) {
      pIdx++
      tIdx++
    } else if (pIdx < pLen && p[pIdx] === '*') {
      starIdx = pIdx
      matchIdx = tIdx
      pIdx++
    } else if (starIdx !== -1) {
      pIdx = starIdx + 1
      matchIdx++
      tIdx = matchIdx
    } else {
      return false
    }
  }

  while (pIdx < pLen && p[pIdx] === '*') {
    pIdx++
  }

  return pIdx === pLen
}

// ─────────────────────────────────────────────
// Type 1：有权限点 + 有岗位 才可访问
// 旁路条件：positionId > 0 直接放行
// ─────────────────────────────────────────────
const TYPE1_URLS = [
  '/auth/archive/getarchivelist',
  '/auth/archive/getcataloglist',
  '/auth/rule/getrulelist',
  '/auth/duty/discuss/create'
]

// ─────────────────────────────────────────────
// Type 2：自己在岗、上级 或 有权限 可访问
// 旁路条件：positionId > 0 || isSuperior
// ─────────────────────────────────────────────
const TYPE2_URLS = [
  '/auth/evaluation/queryUserEvaluationPosition*',
  '/auth/evaluation/editPlan',
  '/auth/evaluation/queryPlan',
  '/auth/evaluation/queryPlan/details',
  '/auth/evaluation/queryPlanSum/details'
]

// ─────────────────────────────────────────────
// Type 3：上级 或 有权限 可访问
// 旁路条件：isSuperior
// ─────────────────────────────────────────────
const TYPE3_URLS = [
  '/auth/evaluation/addEvaluationTask',
  '/auth/evaluation/submitEvaluation'
]

// 上级权限判断依据的 privilege_url（对应 Swift 的 XB6SViewModel.share.isAccessAuth）
const SUPERIOR_PRIVILEGE_URL = '/auth/evaluation/queryEvaluationPosition'

// ─────────────────────────────────────────────
// 内部工具：获取当前公司的 positionId
// 对应 Swift 的 SingletonCompanyModel.positionId
// ─────────────────────────────────────────────
function _getPositionId () {
  const detail = storage.session.get('companyDetail')
  return (detail && detail.positionId > 0) ? detail.positionId : 0
}

// ─────────────────────────────────────────────
// 内部工具：是否为上级/管理者
// 对应 Swift 的 isSuperior = XB6SViewModel.share.isAccessAuth
// 直接在 privilegeList 中查找上级标志 URL，避免递归
// ─────────────────────────────────────────────
function _isSuperior (privilegeList) {
  if (!privilegeList || privilegeList.length === 0) return false
  return privilegeList.some(p =>
    p.privilegeUrl && matchWildcard(p.privilegeUrl, SUPERIOR_PRIVILEGE_URL)
  )
}

// ─────────────────────────────────────────────
// 主函数：检查是否有指定 privilege_url 的访问权限
// 对应 Swift 的 isServiceAuthorized(serviceId:)
//
// 优先级（与 Swift 保持一致）：
//   1. Type 旁路检测（positionId / isSuperior 满足 → 直接放行）
//   2. privilegeList 显式权限匹配（通配符）
//   3. 都不满足 → 拒绝
//
// @param {string} url           接口的 privilege_url，如 '/auth/evaluation/addEvaluationTask'
// @param {object} companyDetail 可选，当前公司详情（含 privilegeList）
// @returns {boolean}            true = 有权限，false = 无权限
// ─────────────────────────────────────────────
export function hasPrivilege (url, companyDetail) {
  const detail = companyDetail || storage.session.get('companyDetail')
  const privilegeList = detail && detail.privilegeList

  const positionId = _getPositionId()
  const superior = _isSuperior(privilegeList)

  // ── Type 1 旁路：positionId > 0 直接放行 ──
  for (const pattern of TYPE1_URLS) {
    if (matchWildcard(pattern, url)) {
      if (positionId > 0) return true
      break // 命中 type1 但条件不满足，跳出继续走 privilegeList
    }
  }

  // ── Type 2 旁路：positionId > 0 或 上级 直接放行 ──
  for (const pattern of TYPE2_URLS) {
    if (matchWildcard(pattern, url)) {
      if (positionId > 0 || superior) return true
      break
    }
  }

  // ── Type 3 旁路：上级 直接放行 ──
  for (const pattern of TYPE3_URLS) {
    if (matchWildcard(pattern, url)) {
      if (superior) return true
      break
    }
  }

  // ── 显式权限匹配（privilegeList 通配符遍历）──
  if (!privilegeList || privilegeList.length === 0) return false

  for (let i = 0; i < privilegeList.length; i++) {
    const privilege = privilegeList[i]
    if (privilege.privilegeUrl && matchWildcard(privilege.privilegeUrl, url)) {
      return true
    }
  }

  return false
}



// WEBPACK FOOTER //
// ./src/common/js/permission.js